import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';

const ActivityDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Mock data - in a real app, this would be fetched from an API
    const activityData = [
      { 
        id: '1',
        date: '2025-03-01',
        title: 'Submitted Math Project', 
        type: 'assignment',
        description: 'Linear Algebra final project submitted via the learning management system.',
        instructor: 'Dr. Robert Chen',
        grade: '92/100',
        feedback: 'Excellent work on the matrix transformations section. The visual representations were particularly helpful. Consider expanding your explanation of eigenvectors in future projects.',
        attachments: ['math_project.pdf', 'calculations.xlsx'],
        submissionTime: '10:42 AM'
      },
      { 
        id: '2',
        date: '2025-02-28',
        title: 'Absence Approved', 
        type: 'leave',
        description: 'One day leave for medical appointment.',
        requestDate: '2025-02-25',
        approvedBy: 'Mrs. Sarah Williams',
        reason: 'Medical appointment',
        status: 'Approved',
        notes: 'Please collect missed assignments from classmates and complete them by March 5th.',
        duration: '1 day'
      },
      { 
        id: '3',
        date: '2025-02-25',
        title: 'Science Quiz Completed', 
        type: 'assessment',
        description: 'Weekly physics quiz on thermodynamics concepts.',
        instructor: 'Mr. James Peterson',
        score: '18/20',
        duration: '30 minutes',
        topics: ['Heat transfer', 'Laws of thermodynamics', 'Entropy'],
        classAverage: '16.2/20',
        feedback: 'Good understanding of key concepts. Review the second law of thermodynamics for better clarity.'
      }
    ];

    // Simulate API fetch with setTimeout
    setTimeout(() => {
      const foundActivity = activityData.find(item => item.id === id);
      setActivity(foundActivity);
      setLoading(false);
    }, 500);
  }, [id]);

  const getBackgroundColor = (type) => {
    switch(type) {
      case 'assignment': return 'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)';
      case 'leave': return 'linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)';
      case 'assessment': return 'linear-gradient(135deg, #fad0c4 0%, #f5a990 100%)';
      default: return 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)';
    }
  };

  const getIcon = (type) => {
    switch(type) {
      case 'assignment': return '📝';
      case 'leave': return '🗓️';
      case 'assessment': return '📊';
      default: return '📌';
    }
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner>Loading...</LoadingSpinner>
      </PageContainer>
    );
  }

  if (!activity) {
    return (
      <PageContainer>
        <ErrorMessage>Activity not found</ErrorMessage>
        <BackButton onClick={() => navigate('/profile')}>Back to Profile</BackButton>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderBar>
        <BackButton onClick={() => navigate('/profile')}>
          <BackIcon>←</BackIcon>
          Back to Profile
        </BackButton>
        <PageTitle>Activity Details</PageTitle>
      </HeaderBar>
      
      <DetailCard style={{ background: getBackgroundColor(activity.type) }}>
        <CardOverlay>
          <ActivityHeader>
            <IconContainer>
              <ActivityIcon>{getIcon(activity.type)}</ActivityIcon>
            </IconContainer>
            <ActivityHeaderContent>
              <ActivityType>{activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}</ActivityType>
              <ActivityTitle>{activity.title}</ActivityTitle>
              <ActivityDate>{formatDate(activity.date)}</ActivityDate>
            </ActivityHeaderContent>
          </ActivityHeader>
          
          <ActivityContent>
            <ActivityDescription>{activity.description}</ActivityDescription>
            
            <DetailList>
              {/* Dynamic fields based on activity type */}
              {Object.entries(activity).map(([key, value]) => {
                // Skip these fields as they're already shown elsewhere
                if(['id', 'date', 'title', 'type', 'description'].includes(key)) {
                  return null;
                }
                
                // Format arrays
                if(Array.isArray(value)) {
                  return (
                    <DetailItem key={key}>
                      <DetailLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</DetailLabel>
                      <DetailValue>
                        <ul>
                          {value.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </DetailValue>
                    </DetailItem>
                  );
                }
                
                return (
                  <DetailItem key={key}>
                    <DetailLabel>{key.charAt(0).toUpperCase() + key.slice(1)}</DetailLabel>
                    <DetailValue>{value}</DetailValue>
                  </DetailItem>
                );
              })}
            </DetailList>
          </ActivityContent>
        </CardOverlay>
      </DetailCard>
      
      <ActionBar>
        <ActionButton primary>Download</ActionButton>
        <ActionButton>Print</ActionButton>
        <ActionButton secondary>Share</ActionButton>
      </ActionBar>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 900px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  background: none;
  border: none;
  color: var(--primary, #3354a5);
  font-weight: 500;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(51, 84, 165, 0.08);
  }
`;

const BackIcon = styled.span`
  margin-right: 0.5rem;
  font-size: 1.1rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-dark, #333);
  margin: 0;
`;

const DetailCard = styled.div`
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
  margin-bottom: 1.5rem;
  position: relative;
`;

const CardOverlay = styled.div`
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  padding: 0;
`;

const ActivityHeader = styled.div`
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  
  @media (max-width: 576px) {
    flex-direction: column;
    text-align: center;
  }
`;

const IconContainer = styled.div`
  flex-shrink: 0;
`;

const ActivityIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  border: 3px solid rgba(255, 255, 255, 0.6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 576px) {
    width: 70px;
    height: 70px;
    font-size: 2rem;
  }
`;

const ActivityHeaderContent = styled.div`
  color: white;
`;

const ActivityType = styled.div`
  display: inline-block;
  background-color: rgba(255, 255, 255, 0.2);
  padding: 0.35rem 0.75rem;
  border-radius: 30px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.75rem;
`;

const ActivityTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
  color: white;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const ActivityDate = styled.div`
  font-size: 1rem;
  opacity: 0.9;
`;

const ActivityContent = styled.div`
  padding: 2rem;
  background-color: white;
`;

const ActivityDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: var(--text-dark, #333);
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid #eaeaea;
`;

const DetailList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  margin-bottom: 0.5rem;
`;

const DetailLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--text-light, #777);
  margin-bottom: 0.25rem;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.div`
  font-size: 1rem;
  color: var(--text-dark, #333);
  
  ul {
    margin: 0;
    padding-left: 1.2rem;
    
    li {
      margin-bottom: 0.25rem;
    }
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  background-color: ${props => 
    props.primary ? 'var(--primary, #3354a5)' : 
    props.secondary ? 'var(--secondary, #e74c3c)' : 
    '#f5f5f5'};
  
  color: ${props => 
    props.primary || props.secondary ? 'white' : 
    'var(--text-dark, #333)'};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    background-color: ${props => 
      props.primary ? '#2a4580' : 
      props.secondary ? '#c0392b' : 
      '#e5e5e5'};
  }
  
  @media (max-width: 576px) {
    width: 100%;
    text-align: center;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  font-size: 1.2rem;
  color: var(--text-light, #777);
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  font-size: 1.2rem;
  color: var(--text-light, #777);
  margin-bottom: 1.5rem;
`;

export default ActivityDetailPage;